/**
 * Serviço de WebSocket para notificações em tempo real
 * NOTA: WebSocket é opcional - a aplicação funciona sem ele usando polling HTTP
 */

// SEMPRE usar a origem atual do browser para garantir compatibilidade com produção
const API_URL = (typeof window !== 'undefined' ? window.location.origin : '');

class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 2; // Reduzido para falhar mais rápido
    this.reconnectDelay = 5000;
    this.listeners = new Map();
    this.isConnected = false;
    this.pingInterval = null;
    this.disabled = false; // Flag para desabilitar após falhas
  }

  /**
   * Conectar ao WebSocket
   */
  connect() {
    // Se já foi desabilitado por falhas, não tentar novamente
    if (this.disabled) {
      console.log('WebSocket: Desabilitado (usar polling HTTP)');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      return; // Silencioso - sem token não conecta
    }

    // Converter URL HTTP para WS
    let wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    wsUrl = `${wsUrl}/api/ws/${token}`;

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('✅ WebSocket: Conectado');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        
        // Iniciar ping/pong para manter conexão
        this.startPing();
        
        // Notificar listeners
        this.emit('connected', { connected: true });
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Emitir evento específico pelo tipo
          if (data.type) {
            this.emit(data.type, data);
          }
          
          // Emitir evento genérico
          this.emit('message', data);
        } catch (e) {
          // Ignorar mensagens não-JSON (pong, etc)
        }
      };

      this.ws.onclose = (event) => {
        this.isConnected = false;
        this.stopPing();
        
        // Notificar listeners
        this.emit('disconnected', { connected: false });
        
        // Tentar reconectar apenas se não foi fechamento intencional
        if (event.code !== 1000 && event.code !== 4001 && event.code !== 4002 && event.code !== 4003) {
          this.attemptReconnect();
        }
      };

      this.ws.onerror = (error) => {
        // Silencioso - erros de WebSocket são comuns e não críticos
        this.emit('error', { error });
      };

    } catch (error) {
      // Falha silenciosa - WebSocket não é crítico
      this.disabled = true;
    }
  }

  /**
   * Desconectar WebSocket
   */
  disconnect() {
    this.stopPing();
    if (this.ws) {
      this.ws.close(1000, 'Desconexão intencional');
      this.ws = null;
    }
    this.isConnected = false;
  }

  /**
   * Tentar reconectar
   */
  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      // Desabilitar WebSocket após máximo de tentativas
      // A aplicação continua a funcionar com polling HTTP
      this.disabled = true;
      console.log('WebSocket: Desabilitado após falhas (usar polling HTTP)');
      return;
    }

    this.reconnectAttempts++;

    setTimeout(() => {
      if (!this.disabled) {
        this.connect();
      }
    }, this.reconnectDelay);
  }

  /**
   * Iniciar ping periódico
   */
  startPing() {
    this.pingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send('ping');
      }
    }, 30000); // Ping a cada 30 segundos
  }

  /**
   * Parar ping
   */
  stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Adicionar listener para eventos
   */
  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType).push(callback);
    
    // Retornar função para remover listener
    return () => this.off(eventType, callback);
  }

  /**
   * Remover listener
   */
  off(eventType, callback) {
    if (this.listeners.has(eventType)) {
      const callbacks = this.listeners.get(eventType);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Emitir evento para listeners
   */
  emit(eventType, data) {
    if (this.listeners.has(eventType)) {
      this.listeners.get(eventType).forEach(callback => {
        try {
          callback(data);
        } catch (e) {
          console.error('Erro no callback WebSocket:', e);
        }
      });
    }
  }

  /**
   * Enviar mensagem pelo WebSocket
   */
  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(typeof data === 'string' ? data : JSON.stringify(data));
    }
  }

  /**
   * Verificar se está conectado
   */
  getIsConnected() {
    return this.isConnected;
  }
}

// Instância singleton
const wsService = new WebSocketService();

export default wsService;
